// src/components/profile/PayFastForm.tsx
"use client";

import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

export interface PayfastData {
    [key: string]: string;
}

interface PayFastFormProps {
  payfastData: PayfastData;
}

const PAYFAST_SVG_URL =
  "https://firebasestorage.googleapis.com/v0/b/zamzi-connect.firebasestorage.app/o/PAYFASTSVG.svg?alt=media&token=726fe1b9-7d99-41bd-80a8-46b4957c54da";

export function PayFastForm({ payfastData }: PayFastFormProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const payfastUrl = payfastData.payfastUrl;
  const [attemptedAutoSubmit, setAttemptedAutoSubmit] = useState(false);

  const submitForm = () => {
    const form = formRef.current;
    if (!form) {
      return;
    }
    form.submit();
  };

  // Auto-submit on mount with a small retry for slow hydration/render cycles.
  useEffect(() => {
    if (!payfastData || !payfastData.signature || !payfastUrl) {
      return;
    }

    const firstTry = window.setTimeout(() => {
      setAttemptedAutoSubmit(true);
      submitForm();
    }, 120);

    const secondTry = window.setTimeout(() => {
      submitForm();
    }, 1200);

    return () => {
      window.clearTimeout(firstTry);
      window.clearTimeout(secondTry);
    };
  }, [payfastData, payfastUrl]);

  if (!payfastData || !payfastData.signature || !payfastUrl) {
      return <Button disabled>Subscription Unavailable</Button>;
  }

  // Keep a visible fallback in case the browser blocks automatic form submission.
  return (
    <div className="flex w-full flex-col items-center justify-center space-y-4">
      <form ref={formRef} action={payfastUrl} method="post" className="w-full">
        {Object.entries(payfastData).map(([name, value]) => {
          if (name !== 'payfastUrl') {
            return <input key={name} type="hidden" name={name} value={value} />;
          }
          return null;
        })}
        <Button type="submit" className="w-full" style={{ backgroundColor: '#FFB81C', color: '#ffffff' }}>
          Continue To Secure Payment
        </Button>
      </form>

      <div className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
        <p className="text-xs font-medium text-slate-600">Secure checkout powered by</p>
        <img
          src={PAYFAST_SVG_URL}
          alt="PayFast secure payments"
          className="mt-1 h-7 w-auto max-w-[150px]"
          loading="lazy"
        />
      </div>

      <div className="flex flex-col items-center justify-center space-y-2">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">
          {attemptedAutoSubmit ? 'If not redirected automatically, use the button above.' : 'Preparing secure redirect...'}
        </p>
      </div>
    </div>
  );
}
